/**
 * User Service - Enterprise-Grade User Management with Tenant Isolation
 * 
 * This service provides comprehensive user management operations with strict
 * tenant isolation enforcement. All operations validate tenant context and
 * ensure users are properly scoped to their company.
 * 
 * @module userService
 * @category Authentication
 * @security CRITICAL - Enforces multi-tenant data isolation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  limit as firestoreLimit
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateEmail as firebaseUpdateEmail,
  User as FirebaseUser
} from 'firebase/auth';
import { auth, db } from '../firebase/client';
import { getCompanyId, isSuperAdmin } from '../tenantContext';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'pumper' | 'mechanic' | 'viewer' | 'contractor' | 'investor';
  phone?: string;
  title?: string;
  department?: string;
  permissions?: string[];
  metadata?: Record<string, any>;
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  role?: 'admin' | 'manager' | 'pumper' | 'mechanic' | 'viewer' | 'contractor' | 'investor';
  phone?: string;
  title?: string;
  department?: string;
  isActive?: boolean;
  permissions?: string[];
  metadata?: Record<string, any>;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  companyId: string;
  phone?: string;
  title?: string;
  department?: string;
  isActive: boolean;
  isSuperAdmin?: boolean;
  permissions?: string[];
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
  metadata?: Record<string, any>;
}

export interface UserListOptions {
  role?: string;
  isActive?: boolean;
  limit?: number;
  orderByField?: 'firstName' | 'lastName' | 'email' | 'createdAt';
  orderDirection?: 'asc' | 'desc';
}

// ============================================================================
// ERROR CLASSES
// ============================================================================

export class UserServiceError extends Error {
  constructor(message: string, public code: string, public details?: any) {
    super(message);
    this.name = 'UserServiceError';
  }
}

export class TenantValidationError extends UserServiceError {
  constructor(message: string, details?: any) {
    super(message, 'TENANT_VALIDATION_ERROR', details);
    this.name = 'TenantValidationError';
  }
}

export class UserNotFoundError extends UserServiceError {
  constructor(userId: string) {
    super(`User not found: ${userId}`, 'USER_NOT_FOUND', { userId });
    this.name = 'UserNotFoundError';
  }
}

export class UnauthorizedError extends UserServiceError {
  constructor(action: string) {
    super(`Unauthorized: ${action}`, 'UNAUTHORIZED', { action });
    this.name = 'UnauthorizedError';
  }
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validates tenant context for user operations
 * @throws {TenantValidationError} If tenant context is invalid
 */
function validateCompanyContext(companyId: string): void {
  if (!companyId) {
    throw new TenantValidationError('Tenant ID is required for user operations');
  }

  if (companyId === 'default') {
    throw new TenantValidationError('Cannot perform user operations with default tenant');
  }

  // SuperAdmin is allowed but should be explicitly handled
  if (companyId === 'superadmin') {
    if (!isSuperAdmin()) {
      throw new UnauthorizedError('SuperAdmin operations require SuperAdmin role');
    }
  }
}

/**
 * Validates user data before creation
 */
function validateCreateUserData(data: CreateUserData): void {
  if (!data.email || !data.email.includes('@')) {
    throw new UserServiceError('Valid email is required', 'INVALID_EMAIL');
  }

  if (!data.password || data.password.length < 8) {
    throw new UserServiceError('Password must be at least 8 characters', 'INVALID_PASSWORD');
  }

  if (!data.firstName || data.firstName.trim().length === 0) {
    throw new UserServiceError('First name is required', 'INVALID_FIRST_NAME');
  }

  if (!data.lastName || data.lastName.trim().length === 0) {
    throw new UserServiceError('Last name is required', 'INVALID_LAST_NAME');
  }

  const validRoles = ['admin', 'pumper', 'mechanic', 'viewer', 'contractor'];
  if (!validRoles.includes(data.role)) {
    throw new UserServiceError(
      `Invalid role. Must be one of: ${validRoles.join(', ')}`,
      'INVALID_ROLE'
    );
  }
}

/**
 * Sanitizes user data for storage
 */
function sanitizeUserData(data: CreateUserData | UpdateUserData): any {
  const sanitized: any = {};

  // Copy allowed fields only
  const allowedFields = [
    'email',
    'firstName',
    'lastName',
    'role',
    'phone',
    'title',
    'department',
    'isActive',
    'permissions',
    'metadata'
  ];

  for (const field of allowedFields) {
    if (field in data && data[field as keyof typeof data] !== undefined) {
      sanitized[field] = data[field as keyof typeof data];
    }
  }

  // Trim string fields
  if (sanitized.firstName) sanitized.firstName = sanitized.firstName.trim();
  if (sanitized.lastName) sanitized.lastName = sanitized.lastName.trim();
  if (sanitized.email) sanitized.email = sanitized.email.trim().toLowerCase();
  if (sanitized.phone) sanitized.phone = sanitized.phone.trim();

  return sanitized;
}

// ============================================================================
// CORE USER OPERATIONS
// ============================================================================

/**
 * Creates a new user with strict tenant isolation
 * 
 * @param userData - User creation data
 * @param creatorUserId - ID of the user creating this user (for audit trail)
 * @returns Promise resolving to the new user's ID
 * 
 * @example
 * ```typescript
 * const userId = await createUserWithTenant({
 *   email: 'john@example.com',
 *   password: 'SecurePass123!',
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   role: 'pumper'
 * }, currentUser.id);
 * ```
 */
export async function createUserWithTenant(
  userData: CreateUserData,
  creatorUserId: string
): Promise<string> {
  try {
    // Step 1: Get and validate tenant context
    const companyId = getCompanyId();
    validateCompanyContext(companyId);
    validateCreateUserData(userData);

    // Step 2: Create Firebase Authentication user
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      userData.email,
      userData.password
    );

    const firebaseUid = userCredential.user.uid;

    // Step 3: Sanitize and prepare user document
    const sanitizedData = sanitizeUserData(userData);
    const userDoc = {
      ...sanitizedData,
      id: firebaseUid,
      companyId: companyId, // CRITICAL: Assign company
      isActive: true,
      isSuperAdmin: false,
      createdBy: creatorUserId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      lastLoginAt: null,
      permissions: userData.permissions || [],
      metadata: {
        ...userData.metadata,
        source: 'user_service',
        creationMethod: 'admin_creation'
      }
    };

    // Step 4: Store in tenant-scoped collection
    const userRef = doc(db, 'companies', companyId, 'users', firebaseUid);
    await setDoc(userRef, userDoc);


    return firebaseUid;

  } catch (error: any) {
    console.error('❌ Error creating user:', error);

    // Handle specific Firebase Auth errors
    if (error.code === 'auth/email-already-in-use') {
      throw new UserServiceError(
        'Email already in use',
        'EMAIL_IN_USE',
        { email: userData.email }
      );
    }

    if (error.code === 'auth/invalid-email') {
      throw new UserServiceError(
        'Invalid email format',
        'INVALID_EMAIL',
        { email: userData.email }
      );
    }

    if (error.code === 'auth/weak-password') {
      throw new UserServiceError(
        'Password is too weak',
        'WEAK_PASSWORD'
      );
    }

    // Re-throw custom errors
    if (error instanceof UserServiceError) {
      throw error;
    }

    // Wrap unknown errors
    throw new UserServiceError(
      'Failed to create user',
      'CREATE_FAILED',
      { originalError: error.message }
    );
  }
}

/**
 * Retrieves a user by ID with tenant validation
 * 
 * @param userId - The user's Firebase UID
 * @param requestingCompanyId - Optional tenant ID (defaults to current tenant)
 * @returns Promise resolving to the user data or null if not found
 */
export async function getUserById(
  userId: string,
  requestingCompanyId?: string
): Promise<AuthUser | null> {
  try {
    const companyId = requestingCompanyId || getCompanyId();
    validateCompanyContext(companyId);

    const userRef = doc(db, 'companies', companyId, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      // For SuperAdmin, try searching across all companies
      if (isSuperAdmin()) {
        return await findUserAcrossTenants(userId);
      }
      return null;
    }

    const data = userSnap.data();
    
    // Verify tenant match (security check)
    if (data.companyId !== companyId && !isSuperAdmin()) {
      return null;
    }

    return {
      id: userSnap.id,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      role: data.role,
      companyId: data.companyId,
      phone: data.phone,
      title: data.title,
      department: data.department,
      isActive: data.isActive ?? true,
      isSuperAdmin: data.isSuperAdmin ?? false,
      permissions: data.permissions || [],
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      lastLoginAt: data.lastLoginAt?.toDate(),
      metadata: data.metadata || {}
    };

  } catch (error: any) {
    console.error(`❌ Error fetching user ${userId}:`, error);
    throw new UserServiceError(
      'Failed to fetch user',
      'FETCH_FAILED',
      { userId, error: error.message }
    );
  }
}

/**
 * Helper function to find user across all tenants (SuperAdmin only)
 */
async function findUserAcrossTenants(userId: string): Promise<AuthUser | null> {
  try {
    // Get all companies
    const companiesRef = collection(db, 'companies');
    const companiesSnap = await getDocs(companiesRef);

    for (const companyDoc of companiesSnap.docs) {
      const userRef = doc(db, 'companies', companyDoc.id, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();
        return {
          id: userSnap.id,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          role: data.role,
          companyId: data.companyId,
          phone: data.phone,
          title: data.title,
          department: data.department,
          isActive: data.isActive ?? true,
          isSuperAdmin: data.isSuperAdmin ?? false,
          permissions: data.permissions || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate(),
          metadata: data.metadata || {}
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error searching across tenants:', error);
    return null;
  }
}

/**
 * Updates an existing user
 * 
 * @param userId - The user's Firebase UID
 * @param updateData - Partial user data to update
 * @param updatingUserId - ID of the user making the update
 * @returns Promise resolving to true on success
 */
export async function updateUser(
  userId: string,
  updateData: UpdateUserData,
  updatingUserId: string
): Promise<boolean> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    // Verify user exists and belongs to tenant
    const existingUser = await getUserById(userId, companyId);
    if (!existingUser) {
      throw new UserNotFoundError(userId);
    }

    if (existingUser.companyId !== companyId && !isSuperAdmin()) {
      throw new UnauthorizedError('Cannot update user from different tenant');
    }

    // Sanitize update data
    const sanitizedData = sanitizeUserData(updateData);

    // Prepare update document
    const updateDoc = {
      ...sanitizedData,
      updatedAt: Timestamp.now(),
      updatedBy: updatingUserId
    };

    // Prevent tenant modification
    delete updateDoc.companyId;
    delete updateDoc.companyId;
    delete updateDoc.id;

    const userRef = doc(db, 'companies', companyId, 'users', userId);
    await updateDoc(userRef, updateDoc);

    return true;

  } catch (error: any) {
    console.error(`❌ Error updating user ${userId}:`, error);

    if (error instanceof UserServiceError) {
      throw error;
    }

    throw new UserServiceError(
      'Failed to update user',
      'UPDATE_FAILED',
      { userId, error: error.message }
    );
  }
}

/**
 * Deletes a user (soft delete by default)
 * 
 * @param userId - The user's Firebase UID
 * @param deletingUserId - ID of the user performing the deletion
 * @param hardDelete - If true, permanently deletes the user
 * @returns Promise resolving to true on success
 */
export async function deleteUser(
  userId: string,
  deletingUserId: string,
  hardDelete: boolean = false
): Promise<boolean> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    // Verify user exists
    const existingUser = await getUserById(userId, companyId);
    if (!existingUser) {
      throw new UserNotFoundError(userId);
    }

    if (existingUser.companyId !== companyId && !isSuperAdmin()) {
      throw new UnauthorizedError('Cannot delete user from different tenant');
    }

    // Prevent self-deletion
    if (userId === deletingUserId) {
      throw new UserServiceError(
        'Cannot delete your own account',
        'SELF_DELETE_FORBIDDEN'
      );
    }

    const userRef = doc(db, 'companies', companyId, 'users', userId);

    if (hardDelete) {
      // Permanent deletion
      await deleteDoc(userRef);
    } else {
      // Soft delete
      await updateDoc(userRef, {
        isActive: false,
        deletedAt: Timestamp.now(),
        deletedBy: deletingUserId,
        updatedAt: Timestamp.now()
      });
    }

    return true;

  } catch (error: any) {
    console.error(`❌ Error deleting user ${userId}:`, error);

    if (error instanceof UserServiceError) {
      throw error;
    }

    throw new UserServiceError(
      'Failed to delete user',
      'DELETE_FAILED',
      { userId, error: error.message }
    );
  }
}

/**
 * Lists all users in a company with filtering and pagination
 * 
 * @param options - Filtering and pagination options
 * @returns Promise resolving to array of users
 */
export async function getUsersByCompany(
  options: UserListOptions = {}
): Promise<AuthUser[]> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const usersRef = collection(db, 'companies', companyId, 'users');
    const constraints: any[] = [];

    // Apply filters
    if (options.role) {
      constraints.push(where('role', '==', options.role));
    }

    if (options.isActive !== undefined) {
      constraints.push(where('isActive', '==', options.isActive));
    }

    // NOTE: Don't add companyId filter - documents are already in tenant-scoped collection
    // The collection path companies/{companyId}/users provides tenant isolation

    // Apply ordering
    const orderField = options.orderByField || 'createdAt';
    const orderDir = options.orderDirection || 'desc';
    constraints.push(orderBy(orderField, orderDir));

    // Apply limit
    if (options.limit) {
      constraints.push(firestoreLimit(options.limit));
    }

    let snapshot;
    try {
      const q = query(usersRef, ...constraints);
      snapshot = await getDocs(q);
    } catch (queryError: any) {
      // If query fails (e.g., missing index), try simpler query
      snapshot = await getDocs(usersRef);
    }
    

    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        companyId: data.companyId || companyId,
        phone: data.phone,
        title: data.title,
        department: data.department,
        isActive: data.isActive ?? true,
        isSuperAdmin: data.isSuperAdmin ?? false,
        permissions: data.permissions || [],
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        lastLoginAt: data.lastLoginAt?.toDate(),
        metadata: data.metadata || {}
      };
    });

  } catch (error: any) {
    console.error('❌ Error fetching users:', error);
    throw new UserServiceError(
      'Failed to fetch users',
      'FETCH_USERS_FAILED',
      { error: error.message }
    );
  }
}

/**
 * Sends a password reset email to a user
 * 
 * @param email - User's email address
 * @returns Promise resolving to true on success
 */
export async function sendPasswordReset(email: string): Promise<boolean> {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error: any) {
    console.error('❌ Error sending password reset:', error);
    throw new UserServiceError(
      'Failed to send password reset email',
      'PASSWORD_RESET_FAILED',
      { email, error: error.message }
    );
  }
}

/**
 * Updates user's last login timestamp
 * 
 * @param userId - The user's Firebase UID
 * @returns Promise resolving to true on success
 */
export async function updateLastLogin(userId: string): Promise<boolean> {
  try {
    const companyId = getCompanyId();
    
    if (!companyId || companyId === 'default') {
      return false;
    }

    const userRef = doc(db, 'companies', companyId, 'users', userId);
    await updateDoc(userRef, {
      lastLoginAt: Timestamp.now()
    });

    return true;
  } catch (error: any) {
    console.error('❌ Error updating last login:', error);
    // Non-critical error, don't throw
    return false;
  }
}

/**
 * Batch creates multiple users (useful for imports)
 * 
 * @param usersData - Array of user creation data
 * @param creatorUserId - ID of the user creating these users
 * @returns Promise resolving to array of created user IDs
 */
export async function batchCreateUsers(
  usersData: CreateUserData[],
  creatorUserId: string
): Promise<{ success: string[]; failed: Array<{ email: string; error: string }> }> {
  const results = {
    success: [] as string[],
    failed: [] as Array<{ email: string; error: string }>
  };

  for (const userData of usersData) {
    try {
      const userId = await createUserWithTenant(userData, creatorUserId);
      results.success.push(userId);
    } catch (error: any) {
      results.failed.push({
        email: userData.email,
        error: error.message || 'Unknown error'
      });
    }
  }


  return results;
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  createUserWithTenant,
  getUserById,
  updateUser,
  deleteUser,
  getUsersByCompany,
  sendPasswordReset,
  updateLastLogin,
  batchCreateUsers
};
